import { DatalayerService } from '@analytics/datalayer';
import type { AnalyticsRendering } from '@analytics/models';
import {
  AnalyticsUtils,
  getComponentNameFromAnalyticsRendering,
} from '@analytics/utils';
import { Directive, HostListener, inject, Input } from '@angular/core';
import type { ImageFieldValue, LinkFieldValue } from '@core/jss-models';

type TrackLinkInfo = {
  field: ImageFieldValue | LinkFieldValue | string;
  type:
    | 'text'
    | 'image'
    | 'button'
    | 'dropdown'
    | 'communicatie'
    | 'navigation';
  eventType?: 'link-click' | 'button-click' | 'files-download';
  rendering?: AnalyticsRendering;
};

@Directive({
  selector: '[wlTrackLink]',
  standalone: true,
})
export class TrackLinkDirective {
  readonly #datalayer = inject(DatalayerService);
  readonly #analyticsUtils = inject(AnalyticsUtils);

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input('wlTrackLink') trackLink = 'unknown-link';
  private readonly defaultEventType = 'link-click';

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input({ required: true }) trackLinkInfo!: TrackLinkInfo;

  @HostListener('click') onClick() {
    this.sendEvent();
  }

  private async getEventInfo(
    componentName: string,
    field: TrackLinkInfo['field'],
    type?: TrackLinkInfo['type']
  ) {
    const placement =
      await this.#analyticsUtils.getElementPlacement(componentName);

    if (field == null) {
      return {};
    } else if (typeof field === 'string') {
      return { name: field, type, placement };
    } else if ('alt' in field) {
      return { name: field['alt'], type, placement };
    } else {
      return { name: field['text'], type, placement };
    }
  }

  private async sendEvent() {
    const { field, type, rendering } = this.trackLinkInfo;
    const componentName = getComponentNameFromAnalyticsRendering(rendering);

    this.#datalayer.pushEvent({
      event: this.trackLinkInfo.eventType ?? this.defaultEventType,
      info: await this.getEventInfo(componentName, field, type),
    });
  }
}
